import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Plus } from "lucide-react"
import { useNavigate } from "react-router-dom"
import ServiceCard from "@/pages/services/components/ServiceCard"
import {
  useFetchServices,
  useDeleteService,
} from "@/services/query/services.query"
import { useState, useCallback } from "react"
import { useToast } from "@/components/ui/use-toast"
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { AddServiceDialog } from "@/components/services/AddServiceDialog"
import { EditServiceDialog } from "@/components/services/EditServiceDialog"
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from "@/components/ui/alert-dialog"

function ServicePage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [pagination, setPagination] = useState({ offset: 0, limit: 9 })
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [selectedService, setSelectedService] = useState(null)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [search, setSearch] = useState("")
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  // Debounce search
  const debouncedSearch = useCallback(
    debounce((value) => {
      setSearch(value)
      setPagination((prev) => ({ ...prev, offset: 0 }))
    }, 500),
    []
  )

  const { data, isLoading, isError, error } = useFetchServices(
    pagination.offset,
    pagination.limit,
    search
  )

  const deleteServiceMutation = useDeleteService()

  const handlePageChange = (newOffset) => {
    setPagination((prev) => ({ ...prev, offset: newOffset }))
  }

  const handleCardClick = (serviceId) => {
    navigate(`/services/${serviceId}`)
  }

  const handleEdit = (service) => {
    setSelectedService(service)
    setIsEditDialogOpen(true)
  }

  const handleDelete = async () => {
    if (!selectedService) return

    try {
      await deleteServiceMutation.mutateAsync(selectedService.id)
      toast({
        title: "Success",
        description: "Service deleted successfully",
      })
      setDeleteDialogOpen(false)
      setSelectedService(null)
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to delete service",
        variant: "destructive",
      })
    }
  }

  const handleSearchChange = (e) => {
    debouncedSearch(e.target.value)
  }

  return (
    <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
        <div className="w-full sm:w-auto sm:flex-1 max-w-sm">
          <Input
            type="search"
            placeholder="Search services..."
            onChange={handleSearchChange}
            className="w-full"
          />
        </div>
        <Button
          onClick={() => setIsDialogOpen(true)}
          className="w-full sm:w-auto bg-blue-500 hover:bg-blue-600"
        >
          <Plus className="mr-2 h-4 w-4" /> Add Service
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
        {isLoading ? (
          Array(3)
            .fill(0)
            .map((_, idx) => (
              <div key={idx} className="animate-pulse">
                <div className="h-[200px] bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              </div>
            ))
        ) : isError ? (
          <div className="col-span-full p-4 text-center bg-red-50 dark:bg-red-900/10 rounded-lg">
            <p className="text-red-600 dark:text-red-400">
              Error: {error.message}
            </p>
          </div>
        ) : data?.services?.length === 0 ? (
          <div className="col-span-full p-8 text-center bg-gray-50 dark:bg-gray-800/50 rounded-lg">
            <p className="text-gray-600 dark:text-gray-400">
              No services found
            </p>
          </div>
        ) : (
          data?.services?.map((service) => (
            <ServiceCard
              key={service.id}
              service={service}
              onEdit={() => handleEdit(service)}
              onDelete={() => {
                setSelectedService(service)
                setDeleteDialogOpen(true)
              }}
            />
          ))
        )}
      </div>

      {data && data.pagination.totalPages > 1 && (
        <div className="mt-8 flex justify-center overflow-x-auto">
          <Pagination className="py-2">
            <PaginationContent className="flex-wrap gap-2">
              <PaginationItem>
                <PaginationPrevious
                  onClick={() =>
                    handlePageChange(
                      Math.max(0, pagination.offset - pagination.limit)
                    )
                  }
                  disabled={pagination.offset === 0}
                />
              </PaginationItem>
              {[...Array(data.pagination.totalPages)].map((_, idx) => (
                <PaginationItem key={idx}>
                  <PaginationLink
                    onClick={() => handlePageChange(idx * pagination.limit)}
                    isActive={pagination.offset === idx * pagination.limit}
                  >
                    {idx + 1}
                  </PaginationLink>
                </PaginationItem>
              ))}
              <PaginationItem>
                <PaginationNext
                  onClick={() =>
                    handlePageChange(pagination.offset + pagination.limit)
                  }
                  disabled={
                    pagination.offset + pagination.limit >=
                    data.pagination.total
                  }
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        </div>
      )}

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. This will permanently delete the
              service.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AddServiceDialog open={isDialogOpen} onOpenChange={setIsDialogOpen} />
      <EditServiceDialog
        service={selectedService}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
      />
    </div>
  )
}

// Debounce utility function
function debounce(func, wait) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

export default ServicePage
